import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { CaretDown, CaretUp, ShieldCheck, Warning } from 'phosphor-react';
import { IIssue, IssueSeveridade, useSocietario } from '../../../../../hooks/useSocietario';

interface IssuesPanelProps {
  contratoId: number;
  /** Apenas exibe o engine no header — nao muda comportamento.
   * Schema-extension v3 elo 5: 'v3' adicionado. Componente continua
   * sendo renderizado pra qualquer engine — mostra issues do gate
   * validators que rodam pra todos. */
  engine?: 'v1' | 'v2' | 'v3' | null;
  /** Comeca expandido. Default true (revisao precisa atencao imediata). */
  defaultOpen?: boolean;
}

const SEVERIDADE_LABEL: Record<IssueSeveridade, string> = {
  critico: 'Crítico',
  sugestao: 'Sugestão',
  forma: 'Forma',
};

const SEVERIDADE_COLOR: Record<IssueSeveridade, string> = {
  critico: 'red',
  sugestao: 'orange',
  forma: 'gray',
};

// Ordem de exibicao — criticos primeiro.
const SEVERIDADE_ORDER: IssueSeveridade[] = ['critico', 'sugestao', 'forma'];

interface IGroup {
  severidade: IssueSeveridade;
  items: IIssue[];
}

function IssueCard({ iss, severidade }: { iss: IIssue; severidade: IssueSeveridade }) {
  const color = SEVERIDADE_COLOR[severidade];
  return (
    <Box
      borderWidth="1px"
      borderColor={`${color}.200`}
      borderLeftWidth="3px"
      borderLeftColor={`${color}.500`}
      borderRadius="md"
      p={3}
      bg={`${color}.50`}
    >
      <Flex justify="space-between" align="flex-start" gap={2} mb={1}>
        <HStack spacing={2} flexWrap="wrap">
          <Badge fontSize="2xs" variant="subtle" colorScheme="gray">
            {iss.codigo}
          </Badge>
          <Badge fontSize="2xs" variant="outline" colorScheme="gray">
            camada {iss.camada}
          </Badge>
          {iss.overridable ? (
            <Badge fontSize="2xs" colorScheme="blue" variant="subtle">
              substituível
            </Badge>
          ) : null}
        </HStack>
        <Text fontSize="xs" color="gray.500" fontFamily="mono" noOfLines={1}>
          {iss.campo}
        </Text>
      </Flex>
      <Text fontSize="sm" color="gray.800">
        {iss.mensagem}
      </Text>
      {iss.valor_atual || iss.valor_proposto ? (
        <Box mt={2} fontSize="xs" color="gray.600">
          {iss.valor_atual ? (
            <Box>
              <Text as="span" fontWeight="semibold">
                Atual:{' '}
              </Text>
              <Text as="span" fontFamily="mono">
                {iss.valor_atual}
              </Text>
            </Box>
          ) : null}
          {iss.valor_proposto ? (
            <Box>
              <Text as="span" fontWeight="semibold">
                Proposto:{' '}
              </Text>
              <Text as="span" fontFamily="mono">
                {iss.valor_proposto}
              </Text>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function renderBody(args: {
  isLoading: boolean;
  isError: boolean;
  total: number;
  grouped: IGroup[];
  refetch: () => void;
  isFetching: boolean;
}): React.ReactNode {
  const { isLoading, isError, total, grouped, refetch, isFetching } = args;

  if (isLoading) {
    return (
      <Flex align="center" gap={2}>
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.600">
          Rodando validators determinísticos…
        </Text>
      </Flex>
    );
  }
  if (isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Box flex="1">
          <Text fontSize="sm">
            Não foi possível rodar os validators. O contrato foi gerado, mas as checagens não
            rodaram.
          </Text>
          <Button size="xs" mt={2} onClick={() => refetch()} isLoading={isFetching}>
            Tentar novamente
          </Button>
        </Box>
      </Alert>
    );
  }
  if (total === 0) {
    return (
      <Alert status="success" borderRadius="md" variant="subtle">
        <AlertIcon />
        <Text fontSize="sm">
          Nenhum problema detectado pelos validators. Contrato pronto para revisão humana.
        </Text>
      </Alert>
    );
  }
  return (
    <VStack align="stretch" spacing={4}>
      {grouped.map(group => (
        <Box key={group.severidade}>
          <HStack mb={2}>
            <Badge colorScheme={SEVERIDADE_COLOR[group.severidade]} fontSize="xs">
              {SEVERIDADE_LABEL[group.severidade]} ({group.items.length})
            </Badge>
          </HStack>
          <Stack spacing={2}>
            {group.items.map(iss => (
              <IssueCard
                key={`${iss.codigo}|${iss.campo}|${iss.mensagem}`}
                iss={iss}
                severidade={group.severidade}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </VStack>
  );
}

export const IssuesPanel = ({ contratoId, engine, defaultOpen = true }: IssuesPanelProps) => {
  const { validarContrato } = useSocietario();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['societario', 'validar', contratoId],
    queryFn: () => validarContrato(contratoId),
    enabled: !!contratoId,
    staleTime: 30_000,
  });

  const issues: IIssue[] = data ?? [];
  const counts = SEVERIDADE_ORDER.reduce<Record<IssueSeveridade, number>>(
    (acc, sev) => {
      acc[sev] = issues.filter(i => i.severidade === sev).length;
      return acc;
    },
    { critico: 0, sugestao: 0, forma: 0 },
  );
  const total = issues.length;
  const hasCriticos = counts.critico > 0;

  const grouped: IGroup[] = SEVERIDADE_ORDER.map(sev => ({
    severidade: sev,
    items: issues.filter(i => i.severidade === sev),
  })).filter(g => g.items.length > 0);

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" mt={4}>
      <Flex
        align="center"
        justify="space-between"
        p={4}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'gray.50' }}
        borderTopRadius="md"
      >
        <HStack spacing={3}>
          <Icon
            as={hasCriticos ? Warning : ShieldCheck}
            color={hasCriticos ? 'red.500' : 'green.500'}
            boxSize={6}
            weight="duotone"
          />
          <Box>
            <Text fontWeight="bold" fontSize="md">
              Validação automática
              {engine ? (
                <Badge ml={2} fontSize="2xs" colorScheme="purple">
                  engine {engine}
                </Badge>
              ) : null}
            </Text>
            <Text fontSize="xs" color="gray.600">
              {isLoading
                ? 'Rodando validators…'
                : `${total} ${total === 1 ? 'observação' : 'observações'} encontradas`}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2}>
          {SEVERIDADE_ORDER.map(sev =>
            counts[sev] > 0 ? (
              <Badge key={sev} colorScheme={SEVERIDADE_COLOR[sev]} fontSize="xs">
                {counts[sev]} {SEVERIDADE_LABEL[sev].toLowerCase()}
              </Badge>
            ) : null,
          )}
          <Icon as={isOpen ? CaretUp : CaretDown} color="gray.500" />
        </HStack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Box borderTopWidth="1px" borderColor="gray.200" p={4}>
          {renderBody({ isLoading, isError, total, grouped, refetch, isFetching })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default IssuesPanel;
